// Sentry init must be the FIRST import so it can hook the global error
// handlers before any other module loads. `initSentry()` is a no-op when
// `VITE_SENTRY_DSN` is empty — safe to call unconditionally.
import { initSentry } from './utils/sentryInit';
initSentry();

// Deploy-verification beacon (see vite.config.ts `define`). Unconditional in
// BOTH app + shopify builds — if the widget iframe console does NOT show this
// line, or shows an old timestamp, CloudFront is serving a stale/wrong bundle.
console.log('[Prodagi FE] build:', __BUILD_ID__, '| stylist-look: enabled');

// Stale lazy-chunk self-heal. When a code-split chunk 404s after a deploy (an
// old index.html references a hash the CloudFront /*-invalidation deleted),
// Vite fires `vite:preloadError` on window and the route is ALREADY broken —
// so reload once, eagerly, to pull the fresh index.html + new chunk hashes.
// Guarded by a per-build sessionStorage flag so a genuinely missing chunk
// (not just a stale one) can never trigger a reload loop. The softer
// focus/interval "outdated" path is handled by VersionUpdateManager, which
// only auto-refreshes at a safe moment. Registered before React mounts so a
// preload failure during the very first lazy import is still caught.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault(); // suppress the default throw; we recover by reloading
  const key = `reloadedForBuild:${__BUILD_ID__}`;
  try {
    if (sessionStorage.getItem(key)) return; // already self-healed this build
    sessionStorage.setItem(key, '1');
  } catch {
    /* sessionStorage blocked — fall through to a single best-effort reload */
  }
  window.location.reload();
});

import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { BillingProvider } from './contexts/BillingContext';
import { TrialStatusProvider } from './contexts/TrialStatusContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { MaintenanceProvider } from './contexts/MaintenanceContext';

const SentryErrorBoundary = Sentry.ErrorBoundary;

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/*
      Sentry.ErrorBoundary is OUTERMOST so it catches errors thrown by any
      provider (Auth/Workspace/Billing — these fetch on mount and can throw)
      AND by any descendant component. When `VITE_SENTRY_DSN` is empty,
      Sentry.init never ran but the boundary still mounts as a regular React
      ErrorBoundary — the captureException calls are no-ops without a DSN.
      Replaces the global "white screen on crash" with a recoverable fallback.
    */}
    <SentryErrorBoundary
      fallback={({ resetError }) => (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            background: '#f9fafb',
            color: '#111827',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
            Something went wrong.
          </h1>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px', maxWidth: '420px' }}>
            We&apos;ve been notified and are looking into it. You can try again, or reload the page.
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={resetError}
              style={{
                padding: '8px 16px',
                background: '#1e73be',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      )}
    >
      {/*
        AccessibilityProvider is outermost so the user's font-size / contrast /
        motion / dyslexic-font preferences land on <html> before anything else
        paints (auth-gated routes, workspace fetches, billing hydration). Per
        Plan-AccessibilitySettings.md §2.
      */}
      <AccessibilityProvider>
        {/*
          MaintenanceProvider sits high in the tree alongside Accessibility
          because the ongoing-maintenance blocker must render even when
          downstream providers (auth / workspace / billing) are mid-fetch
          — the customer should see the maintenance modal regardless of
          their auth state once the operator has flipped the window on.
          Plan-MaintenanceBanners.md §E.1.
        */}
        <MaintenanceProvider>
          <ThemeProvider>
            <AuthProvider>
              <WorkspaceProvider>
                <ToastProvider>
                  <BillingProvider>
                    {/*
                      TrialStatusProvider derives isExpired / daysRemaining /
                      ownerEmail from the billing payload + workspace members.
                      Must sit inside BillingProvider + WorkspaceProvider so
                      it can read both. Plan-TrialExpiration.md §C Phase 2.1.
                    */}
                    <TrialStatusProvider>
                      <App />
                    </TrialStatusProvider>
                  </BillingProvider>
                </ToastProvider>
              </WorkspaceProvider>
            </AuthProvider>
          </ThemeProvider>
        </MaintenanceProvider>
      </AccessibilityProvider>
    </SentryErrorBoundary>
  </React.StrictMode>
);
